import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { scannerApi, apiMessage, extractArray } from 'shared/api/scanner'
import { useAuth } from 'context/AuthContext'
import { PERMISSIONS, hasPermission } from 'shared/util/permissions'
import { PermissionEmpty, Segmented } from 'shared/components/ui/primitives'
import Button from 'shared/components/ui/button'
import Input from 'shared/components/ui/input'
import Label from 'shared/components/ui/label.js'
import { useEvent } from 'context/EventContext'

const SCOPE_OPTIONS = [
  { value: 'e', label: 'Event' },
  { value: 'pl', label: 'Plan' },
  { value: 'pa', label: 'Pass' },
  { value: 'g', label: 'Global' }
]

function toDateInput(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 16)
}

function CouponForm() {
  const { eventId, couponId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { event } = useEvent()
  const editing = Boolean(couponId && couponId !== 'new')
  const existing = location.state?.coupon
  const [plans, setPlans] = useState([])
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    sCode: existing?.sCode || '',
    nCount: existing?.nCount || 10,
    sTermCondition: existing?.sTermCondition || 'Valid for this event only.',
    dExpireDate: toDateInput(existing?.dExpireDate),
    eCouponType: existing?.eCouponType || 'e',
    nDiscount: existing?.nDiscount || '',
    eDiscountType: existing?.eDiscountType || 'P',
    nMaxDiscountAmount: existing?.nMaxDiscountAmount || '',
    nMinAmountToApplyCode: existing?.nMinAmountToApplyCode || '',
    iPlanId: existing?.iPlanId || ''
  })

  const canSave = hasPermission(user, eventId, editing ? PERMISSIONS.EDIT_COUPON : PERMISSIONS.ADD_COUPON)

  useEffect(() => {
    scannerApi
      .plans(eventId)
      .then((res) => setPlans(extractArray(res, ['aPlan', 'aPlans'])))
      .catch(() => {})
  }, [eventId])

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const onSubmit = async (e) => {
    e.preventDefault()
    if (form.sCode.trim().length < 4) {
      toast.error('Code must be at least 4 characters.')
      return
    }
    setSaving(true)
    const body = {
      ...form,
      sCode: form.sCode.trim().toUpperCase(),
      nCount: Number(form.nCount),
      nDiscount: Number(form.nDiscount),
      nMaxDiscountAmount: form.eDiscountType === 'P' ? Number(form.nMaxDiscountAmount || 0) : undefined,
      nMinAmountToApplyCode: Number(form.nMinAmountToApplyCode || 0),
      dExpireDate: form.dExpireDate ? new Date(form.dExpireDate).toISOString() : undefined,
      iEventId: eventId,
      eCreatedBy: 'org'
    }
    if (form.eCouponType !== 'pl') delete body.iPlanId
    if (editing) body._id = couponId
    try {
      if (editing) await scannerApi.editCoupon(eventId, body)
      else await scannerApi.createCoupon(eventId, body)
      toast.success(editing ? 'Coupon updated' : 'Coupon created')
      navigate(`/events/${eventId}/coupons`)
    } catch (err) {
      toast.error(apiMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (!canSave) return <PermissionEmpty />

  return (
    <div className="mx-auto w-full max-w-lg px-4 py-5">
      <h2 className="mb-1 text-4xl font-bold">{editing ? 'Edit coupon' : 'Create coupon'}</h2>
      <p className="mb-5 text-muted">{event?.sTitle}</p>
      <form onSubmit={onSubmit} className="space-y-5">
        <section className="card-surface space-y-3 p-4">
          <h3 className="mb-2 text-xl font-semibold">Code & validity</h3>
          <div>
            <Label>Code</Label>
            <Input value={form.sCode} onChange={(e) => setField('sCode', e.target.value.toUpperCase())} placeholder="SAVE20" />
          </div>
          <div>
            <Label>Usage count</Label>
            <Input type="number" min="1" value={form.nCount} onChange={(e) => setField('nCount', e.target.value)} />
          </div>
          <div>
            <Label>Expires</Label>
            <Input type="datetime-local" value={form.dExpireDate} onChange={(e) => setField('dExpireDate', e.target.value)} />
          </div>
          <div>
            <Label>Terms</Label>
            <textarea className="field-input min-h-[96px] py-3" value={form.sTermCondition} onChange={(e) => setField('sTermCondition', e.target.value)} />
          </div>
        </section>

        <section className="card-surface space-y-3 p-4">
          <h3 className="mb-2 text-xl font-semibold">Discount</h3>
          <Segmented
            value={form.eDiscountType}
            onChange={(v) => setField('eDiscountType', v)}
            options={[
              { value: 'P', label: 'Percent' },
              { value: 'F', label: 'Flat' }
            ]}
          />
          <div>
            <Label>{form.eDiscountType === 'P' ? 'Percent' : 'Amount'}</Label>
            <Input type="number" min="0" step="0.01" value={form.nDiscount} onChange={(e) => setField('nDiscount', e.target.value)} />
          </div>
          {form.eDiscountType === 'P'
            ? (
            <div>
              <Label>Max discount cap</Label>
              <Input type="number" min="0" value={form.nMaxDiscountAmount} onChange={(e) => setField('nMaxDiscountAmount', e.target.value)} />
            </div>
              )
            : (
            <div>
              <Label>Min cart value</Label>
              <Input type="number" min="0" value={form.nMinAmountToApplyCode} onChange={(e) => setField('nMinAmountToApplyCode', e.target.value)} />
            </div>
              )}
        </section>

        <section className="card-surface space-y-3 p-4">
          <h3 className="mb-2 text-xl font-semibold">Scope</h3>
          <Segmented value={form.eCouponType} onChange={(v) => setField('eCouponType', v)} options={SCOPE_OPTIONS} />
          {form.eCouponType === 'pl' && (
            <div>
              <Label>Plan</Label>
              <select className="field-input" value={form.iPlanId} onChange={(e) => setField('iPlanId', e.target.value)}>
                <option value="">Select plan</option>
                {(Array.isArray(plans) ? plans : []).map((plan) => (
                  <option key={plan._id} value={plan._id}>
                    {plan.sTitle}
                  </option>
                ))}
              </select>
            </div>
          )}
        </section>

        <Button className="w-full" type="submit" disabled={saving}>
          {saving ? 'Saving...' : editing ? 'Save changes' : 'Create coupon'}
        </Button>
      </form>
    </div>
  )
}

export default CouponForm
